import type { Database } from "@medceptor/supabase/types";

export type UserAudience = Database["public"]["Enums"]["user_audience"];
export type UserTrack = Database["public"]["Enums"]["user_track"];

/** The full multi-profession credential classifier — the `content_items.cert_level`
 *  enum (every cert across professions, incl. CNA/LPN/RN). Type + values come from
 *  the GENERATED `Constants` (per rubric/types.ts), not a hand-maintained tuple.
 *  Distinct from scenarios' EMS-only `CertLevel` rubric axis: this is the
 *  content/gating cert set. */
export type ContentCertLevel = Database["public"]["Enums"]["cert_level"];

/** Maps a user's study TRACK to the content `cert_level` whose content they see
 *  in the catalog. USUALLY 1:1, but several tracks CAN share one content cert:
 *  Nurse / MedStudent default to EMT-level foundational content, and the `CRT`
 *  track maps to the SAME `RRT` content cert as the `RRT` track — CRT and RRT are
 *  earned off the identical NBRC TMC written exam, so they share one TMC question
 *  bank verbatim (no content duplication). The CRT-vs-RRT product difference is
 *  handled by the TRACK, not the cert: `trackOffersScenarios` hides the
 *  CSE/scenarios for the written-exam-only CRT track. Strict Record so a new
 *  user_track value is a compile error until mapped. */
export const CERT_FOR_TRACK: Record<UserTrack, ContentCertLevel> = {
  "EMT-B": "EMT",
  AEMT: "AEMT",
  Paramedic: "Paramedic",
  CNA: "CNA",
  LPN: "LPN",
  RN: "RN",
  CCRN: "CCRN",
  CEN: "CEN",
  RRT: "RRT",
  CRT: "RRT", // shares the RRT/TMC content cert — see trackOffersScenarios
  "FNP-C": "FNP-C", // Family Nurse Practitioner (AANP board) — internal-only
  "FNP-BC": "FNP-BC", // Family Nurse Practitioner (ANCC board) — internal-only
  Nurse: "EMT",
  MedStudent: "EMT",
};

/** Tracks that are WRITTEN-EXAM-ONLY — they share a content cert with a sibling
 *  track but must NOT see that cert's clinical simulations. Today: the `CRT`
 *  track shares the `RRT` content cert but a Certified Respiratory Therapist does
 *  NOT sit the CSE clinical-simulation exam (only the RRT does), so CRT students
 *  see the TMC question bank + exam but never the CSE/scenarios. `RRT` (and every
 *  other track) keeps its scenarios. Consulted by the Scenarios nav item + the
 *  /scenarios route gate. */
const TRACKS_WITHOUT_SCENARIOS: ReadonlySet<UserTrack> = new Set(["CRT"]);

/** Whether the viewer's study track includes clinical-simulation scenarios.
 *  Track-level (not cert-level) because CRT + RRT share the RRT content cert but
 *  differ here. `null` track (pre-onboarding / legacy) → true (unchanged
 *  all-scenarios default). */
export function trackOffersScenarios(track: UserTrack | null): boolean {
  return track === null || !TRACKS_WITHOUT_SCENARIOS.has(track);
}

// Audience radio options. Order matters — student first because B2C
// is the dominant signup path; instructor second since they're the
// B2B handoff signal.
export const AUDIENCE_OPTIONS: ReadonlyArray<{
  value: UserAudience;
  label: string;
  description: string;
}> = [
  {
    value: "student",
    label: "Student",
    description: "I'm preparing for my certification exam.",
  },
  {
    value: "instructor",
    label: "Instructor",
    description: "I teach certification courses.",
  },
];

// Track dropdown — the selectable study tracks. Most map 1:1 to a content
// `cert_level` via CERT_FOR_TRACK; the respiratory pair is the exception (CRT +
// RRT both map to the RRT content cert — same TMC question bank, differing only
// in the CSE/scenario surface). As of the 2026-07-08 FNP launch every listed
// track is public. Tracks whose cert is in INTERNAL_ONLY_CERTS are hidden from
// the dropdowns AND rejected by both server actions via `trackIsSelectable`
// (cert-visibility.ts) — that predicate is the boundary, this list is data.
// The generic `Nurse` / `MedStudent` placeholders were removed now that real
// tracks exist (their orphaned enum values stay mapped to EMT in CERT_FOR_TRACK
// for any legacy user still on them, but aren't pickable).
export const TRACK_OPTIONS: ReadonlyArray<{
  value: UserTrack;
  label: string;
  /** One-line plain-language name under the label on the onboarding track
   *  picker (the label alone is an acronym a first-time visitor may not map
   *  to their exam). */
  description: string;
  available: boolean;
}> = [
  { value: "EMT-B", label: "EMT-B", description: "National Registry EMT", available: true },
  { value: "AEMT", label: "AEMT", description: "Advanced EMT", available: true },
  { value: "Paramedic", label: "Paramedic", description: "National Registry Paramedic", available: true },
  { value: "CNA", label: "CNA", description: "Certified Nursing Assistant", available: true },
  { value: "LPN", label: "LPN", description: "Practical nurse, NCLEX-PN", available: true },
  { value: "RN", label: "RN", description: "Registered nurse, NCLEX-RN", available: true },
  { value: "CCRN", label: "CCRN", description: "Critical care nursing", available: true },
  { value: "CEN", label: "CEN", description: "Emergency nursing", available: true },
  // Respiratory therapy — CRT + RRT share the TMC question bank; the CRT track is
  // written-exam-only (no CSE/scenarios, see trackOffersScenarios), the RRT track
  // adds the CSE clinical-simulation scenarios.
  { value: "CRT", label: "CRT", description: "Certified respiratory therapist", available: true },
  { value: "RRT", label: "RRT", description: "Registered respiratory therapist", available: true },
  // Family Nurse Practitioner — two boards, one credential (AANP FNP-C vs ANCC
  // FNP-BC). Launched public 2026-07-08 (INTERNAL_ONLY_CERTS empty), so both are
  // publicly selectable.
  { value: "FNP-C", label: "FNP-C", description: "Family nurse practitioner, AANP", available: true },
  { value: "FNP-BC", label: "FNP-BC", description: "Family nurse practitioner, ANCC", available: true },
];

/** Element shape of TRACK_OPTIONS — the one name for it (consumed by the
 *  track dropdowns + `selectableTrackOptions`); derive, don't re-spell. */
export type TrackOption = (typeof TRACK_OPTIONS)[number];

// Referral-source dropdown options. Per CLAUDE.md "referral_source
// switched from enum to text", the DB column is text + frontend
// curates the options here. Adding a new source = one line edit, no
// migration. Order: highest-volume first (social platforms), then
// search/AI, then word-of-mouth, then "Other" sentinel last.
export const REFERRAL_SOURCE_OPTIONS = [
  "Facebook",
  "Reddit",
  "TikTok",
  "Instagram",
  // Added 2026-07-30: the most common free-text "Other" answer (20 signups in
  // 90 days, spelled both "YouTube" and "Youtube"), which is exactly the drift
  // a curated option removes. Safe as a pure TS edit: the column is
  // `text check (length between 1 and 64)`, not an enum, and the Zod schema
  // derives from this array (docs/utm-conventions.md).
  "YouTube",
  "Word of mouth",
  "Google search",
  "AI / ChatGPT",
  "Other",
] as const;

export type ReferralSource = (typeof REFERRAL_SOURCE_OPTIONS)[number];

export const REFERRAL_SOURCE_OTHER: ReferralSource = "Other";

/** Encode the user's referral pick + (when "Other") their free-text
 *  note into the single text column. Format: `"Other: <note>"` so
 *  analytics can grep for `^Other:` to surface free-text answers
 *  without an additional schema column. Returns `null` when the user
 *  skipped the (optional) source pick — DB column is nullable. */
export function encodeReferralSource(
  source: ReferralSource | "" | undefined,
  otherNote: string,
): string | null {
  if (!source) return null;
  if (source !== REFERRAL_SOURCE_OTHER) return source;
  const trimmed = otherNote.trim();
  return trimmed ? `Other: ${trimmed}` : "Other";
}

/** The professions the onboarding groups tracks under: the ones with a
 *  selectable track today (`professionForCert` also knows future ones, e.g.
 *  pharmacy, that have no track yet). */
export const PROFESSION_OPTIONS = [
  { value: "ems", label: "EMS" },
  { value: "nursing", label: "Nursing" },
  { value: "respiratory", label: "Respiratory" },
] as const;
export type Profession = (typeof PROFESSION_OPTIONS)[number]["value"];

/** A track's profession. Spelled out (not computed with `professionForCert`)
 *  because this module ships to the browser and `@medceptor/scenarios/
 *  question-schema` is ~1,500 lines of zod that can't tree-shake. It is NOT a
 *  second owner: constants.test.ts asserts every entry equals
 *  `professionForCert(CERT_FOR_TRACK[t])`, the mapping content is tagged
 *  with, so a drift fails CI. */
export const PROFESSION_FOR_TRACK: Record<UserTrack, Profession> = {
  "EMT-B": "ems",
  AEMT: "ems",
  Paramedic: "ems",
  CNA: "nursing",
  LPN: "nursing",
  RN: "nursing",
  CCRN: "nursing",
  CEN: "nursing",
  CRT: "respiratory",
  RRT: "respiratory",
  "FNP-C": "nursing",
  "FNP-BC": "nursing",
  Nurse: "ems",
  MedStudent: "ems",
};

export function professionForTrack(track: UserTrack): Profession {
  return PROFESSION_FOR_TRACK[track];
}

// ── Guided-plan answers (onboarding_responses, migration 20261001060000) ──
// Stored as these KEYS (text / text[] / smallint, CHECK-guarded shape only).
// Labels can be reworded freely; renaming a KEY orphans stored rows, so add a
// new key instead. Zod derives its enums from these arrays.

export const EXAM_TIMEFRAME_OPTIONS = [
  { value: "within_2_weeks", label: "In the next 2 weeks" },
  { value: "within_month", label: "Within a month" },
  { value: "one_to_three_months", label: "1 to 3 months out" },
  { value: "not_scheduled", label: "Not scheduled yet" },
] as const;
export type ExamTimeframe = (typeof EXAM_TIMEFRAME_OPTIONS)[number]["value"];

/** Test-day worries. `professions` scopes which ones a profession is shown —
 *  "talking it out" is the NREMT psychomotor station, which nursing and
 *  respiratory exams don't have. */
export const CONCERN_OPTIONS = [
  { value: "critical_step", label: "Missing a critical step", professions: ["ems", "nursing", "respiratory"] },
  { value: "talking_it_out", label: "Freezing when I have to talk it out", professions: ["ems"] },
  { value: "prioritization", label: "Knowing what to do first", professions: ["nursing", "respiratory"] },
  // Not EMS: no EMS scenario or exam runs on a clock today (timer-policy.ts),
  // so the "practice on the clock" answer would promise something absent.
  { value: "time", label: "Running out of time", professions: ["nursing", "respiratory"] },
  { value: "treatment_choice", label: "Knowing which treatment, and when", professions: ["ems", "nursing", "respiratory"] },
  { value: "written_exam", label: "The written exam", professions: ["ems", "nursing", "respiratory"] },
] as const satisfies ReadonlyArray<{
  value: string;
  label: string;
  professions: ReadonlyArray<Profession>;
}>;
export type Concern = (typeof CONCERN_OPTIONS)[number]["value"];

const CONCERN_VALUES: ReadonlySet<string> = new Set(CONCERN_OPTIONS.map((o) => o.value));
/** Narrows a stored value to a current key. `concerns` is text[] in the DB
 *  (shape-checked only), so a read must not trust it: a retired key, or a row
 *  edited through the API, would otherwise index copy tables as undefined. */
export function isConcern(value: string): value is Concern {
  return CONCERN_VALUES.has(value);
}

export function concernOptionsFor(profession: Profession) {
  return CONCERN_OPTIONS.filter((o) =>
    (o.professions as ReadonlyArray<Profession>).includes(profession),
  );
}

/** Scenarios-per-week stops on the pace slider. 1..21 is the DB CHECK. */
export const WEEKLY_SCENARIO_GOAL_OPTIONS = [3, 5, 7, 10] as const;
type WeeklyScenarioGoal = (typeof WEEKLY_SCENARIO_GOAL_OPTIONS)[number];
export const DEFAULT_WEEKLY_SCENARIO_GOAL: WeeklyScenarioGoal = 5;
